<template>
  <PageTemplate>
    <Loader v-if="!isLoaded"/>
    <div v-else>
      <BackButton
          :label="$t('general.button.back')"
          :to="{ name: 'analyticsHome' }"
          marginBottom="5"/>

      <div class="d-flex justify-content-between">
        <h6 class="element-header">
          Prognose
        </h6>
      </div>

      <div class="element-box">
        <h5 class="form-header mb-4">Prognose - Einzelcoaching</h5>

        <Search
            :filters="filters"
            :loading="isSearching"
            :officeDropdown="officeDropdown"
            :unemployedDropdown="unemployedDropdown"
            :clientDropdown="clientDropdown"
            @search="getTotalValues"/>


        <table class="table table-borderless w-50 w-md-75 mt-4">
          <thead>
          <tr>
            <th class="text-start" style="font-size: 12px;">Maßnahmenn</th>
            <th class="text-end" style="font-size: 12px;">Summeneinheit</th>
            <th class="text-end" style="font-size: 12px;">Gesamtwertes</th>
          </tr>
          </thead>
          <tbody>
          <tr>
            <td class="text-start pb-1">Einzelcoaching</td>
            <td class="text-end pb-1">{{totalValues.privateCoachingSumUnits}}</td>
            <td class="text-end pb-1">{{totalValues.privateCoachingTotalAmount}}<span class="ms-1">&euro;</span></td>
            <td>
              <ExcelExportButton
                  :label="$t('general.excel_export')"
                  :loading="isExcelLoaded1"
                  @click="getExcelExport1"/>
            </td>
          </tr>
          <tr>
            <td class="text-start pb-1">Gruppencoaching</td>
            <td class="text-end pb-1">{{totalValues.groupCoachingSumUnits}}</td>
            <td class="text-end pb-1">{{totalValues.groupCoachingTotalAmount}}<span class="ms-1">&euro;</span></td>
            <td>
              <ExcelExportButton
                  :label="$t('general.excel_export')"
                  :loading="isExcelLoaded2"
                  @click="getExcelExport2"/>
            </td>
          </tr>
          <tr>
            <td class="text-start pb-1"> Soziotherapie</td>
            <td class="text-end pb-1">{{totalValues.socioTherapyCoachingSumUnits}}</td>
            <td class="text-end pb-1">{{totalValues.socioTherapyCoachingTotalAmount}}<span class="ms-1">&euro;</span></td>
            <td>
              <ExcelExportButton
                  :label="$t('general.excel_export')"
                  :loading="isExcelLoaded3"
                  @click="getExcelExport3"/>
            </td>
          </tr>
          <tr>
            <td class="text-start pb-1">Betreutes Wohnen</td>
            <td class="text-end pb-1">{{totalValues.assistedLivingCoachingSumUnits}}</td>
            <td class="text-end pb-1">{{totalValues.assistedLivingCoachingTotalAmount}}<span class="ms-1">&euro;</span></td>
            <td>
              <ExcelExportButton
                  :label="$t('general.excel_export')"
                  :loading="isExcelLoaded4"
                  @click="getExcelExport4"/>
            </td>
          </tr>
          <tr>
            <td class="text-start pb-1">DRV Fallmanagement</td>
            <td class="text-end pb-1">{{totalValues.caseManagementCoachingSumUnits}}</td>
            <td class="text-end pb-1">{{totalValues.caseManagementCoachingTotalAmount}}<span class="ms-1">&euro;</span></td>
            <td>
              <ExcelExportButton
                  :label="$t('general.excel_export')"
                  :loading="isExcelLoaded5"
                  @click="getExcelExport5"/>
            </td>
          </tr>
          <tr>
            <td class="text-start pb-1">Ergotherapie </td>
            <td class="text-end pb-1">{{totalValues.occupationalTherapyCoachingSumUnits}}</td>
            <td class="text-end pb-1">{{totalValues.occupationalTherapyCoachingTotalAmount}}<span class="ms-1">&euro;</span></td>
            <td>
              <ExcelExportButton
                  :label="$t('general.excel_export')"
                  :loading="isExcelLoaded6"
                  @click="getExcelExport6"/>
            </td>
          </tr>
          <tr style="border-top: 1px solid #3e4b5b">
            <td class="text-start pb-1"></td>
            <td class="text-end pb-1">{{totalValues.sumUnits}}</td>
            <td class="text-end pb-1">{{totalValues.totalValues}}<span class="ms-1">&euro;</span></td>
          </tr>
          </tbody>
        </table>

      </div>
    </div>
  </PageTemplate>

</template>

<script setup>
import analyticsBillingForecastService from '@/api/Analytics/BillingForecast';
import Loader from '@/components/Loader.vue';
import PageTemplate from '@/components/PageTemplate.vue';
import { CloseToast, ErrorToast } from '@/components/VueToast';
import { getAllUnemployedDropdown, getClientDropdown, getOfficeDropdown } from '@/shared/dropdowns';
import Search from '@/views/App/Analytics/Accounting/Search.vue';
import { onMounted, ref } from 'vue';
import {downloadFile} from "@/shared/functions";
import ExcelExportButton from "@/components/ExcelExportButton.vue";
import BackButton from "@/components/button/BackButton.vue";

const currentDate = new Date().getFullYear();

const filters = ref({
  'officeIds[]': [],
  clientId: '',
  unemployedId: '',
  startDate: `01.01.${currentDate}`,
  endDate: `31.12.${currentDate}`,
});

const totalValues        = ref({});
const officeDropdown     = ref([]);
const clientDropdown     = ref([]);
const unemployedDropdown = ref([]);
const isSearching        = ref(false);
const isLoaded           = ref(false);
const isExcelLoaded1     = ref(false);
const isExcelLoaded2     = ref(false);
const isExcelLoaded3     = ref(false);
const isExcelLoaded4     = ref(false);
const isExcelLoaded5     = ref(false);
const isExcelLoaded6     = ref(false);

async function getTotalValues(page = 1, perPage, passedFilters) {
  CloseToast();

  if (passedFilters) {
    isSearching.value = true;
  }

  try {
    const { data } = await analyticsBillingForecastService.getTotalValues({
      ...(passedFilters ? passedFilters : filters.value),
    });

    totalValues.value = data.data
    isSearching.value  = false;
  } catch (error) {
    await ErrorToast(error);
    isSearching.value = false;
  }
}

async function getExcelExport1() {
  await downloadFile('/analytics/billing-forecast/pr-total-values-excel-export', undefined, filters.value, false, 'GET', isExcelLoaded1)
}
async function getExcelExport2() {
  await downloadFile('/analytics/billing-forecast/gc-total-values-excel-export', undefined, filters.value, false, 'GET', isExcelLoaded2)
}
async function getExcelExport3() {
  await downloadFile('/analytics/billing-forecast/st-total-values-excel-export', undefined, filters.value, false, 'GET', isExcelLoaded3)
}
async function getExcelExport4() {
  await downloadFile('/analytics/billing-forecast/al-total-values-excel-export', undefined, filters.value, false, 'GET', isExcelLoaded4)
}
async function getExcelExport5() {
  await downloadFile('/analytics/billing-forecast/cmc-total-values-excel-export', undefined, filters.value, false, 'GET', isExcelLoaded5)
}
async function getExcelExport6() {
  await downloadFile('/analytics/billing-forecast/ot-total-values-excel-export', undefined, filters.value, false, 'GET', isExcelLoaded6)
}

onMounted(async () => {
  await Promise.all([
    getTotalValues(),
    getOfficeDropdown(officeDropdown),
    getAllUnemployedDropdown(unemployedDropdown),
    getClientDropdown(clientDropdown),
  ]);

  isLoaded.value = true;
});
</script>

<style scoped>

</style>